import { useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const HEARTBEAT_INTERVAL = 30000; // 30 秒更新一次
const ONLINE_THRESHOLD = 60000;   // 60 秒內算線上

export function usePresence(userId: string | null) {
  const updatePresence = useCallback(async (online: boolean) => {
    if (!userId) return;
    await supabase.from('user_presence').upsert({
      user_id: userId,
      is_online: online,
      last_seen_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // 進入頁面立即標記上線
    updatePresence(true);

    // 定時心跳
    const timer = setInterval(() => updatePresence(true), HEARTBEAT_INTERVAL);

    // 頁面可見性變化
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        updatePresence(true);
      } else {
        updatePresence(false);
      }
    };

    // 頁面關閉前標記離線
    const handleBeforeUnload = () => updatePresence(false);

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      updatePresence(false);
    };
  }, [userId, updatePresence]);
}

export async function getLandlordPresence(landlordId: string): Promise<{
  isOnline: boolean;
  lastSeenAt: string | null;
}> {
  const { data } = await supabase
    .from('user_presence')
    .select('is_online, last_seen_at, updated_at')
    .eq('user_id', landlordId)
    .maybeSingle();

  if (!data) return { isOnline: false, lastSeenAt: null };

  const lastSeen = new Date(data.updated_at).getTime();
  const now = Date.now();
  const isOnline = data.is_online && (now - lastSeen) < ONLINE_THRESHOLD;

  return { isOnline, lastSeenAt: data.last_seen_at };
}

export function formatLastSeen(lastSeenAt: string | null): string {
  if (!lastSeenAt) return '尚未上線';
  const diff = Date.now() - new Date(lastSeenAt).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return '剛剛上線';
  if (minutes < 60) return `${minutes} 分鐘前`;
  if (hours < 24) return `${hours} 小時前`;
  return `${days} 天前`;
}
